export type ComponentItem = {
  key: string;
  renderer: () => JSX.Element | null;
};

type ComponentSorterArgs<T extends boolean = boolean> = {
  componentList: ComponentItem[];
  sectionsOrder?:
    | {
        [key: string]: number;
      }
    | string[];
  returnObjectList?: T;
};

export function extractComponentNames(
  components: Array<{ component: string }>,
): string[] {
  return components.map((item) => item.component);
}

function getSortedComponentsBySectionOrder<T extends boolean = false>({
  componentList,
  sectionsOrder,
  returnObjectList = false as T,
}: ComponentSorterArgs<T>): T extends true
  ? ComponentItem[]
  : (JSX.Element | null)[] {
  if (!sectionsOrder) {
    if (returnObjectList) {
      return componentList as T extends true ? ComponentItem[] : never;
    }
    // If no sectionsOrder provided, return all components in original order
    return componentList.map((componentItem) => {
      return componentItem.renderer();
    }) as T extends true ? never : (JSX.Element | null)[];
  }

  // Handle array format (string[])
  if (Array.isArray(sectionsOrder)) {
    const orderMap: { [key: string]: number } = {};
    sectionsOrder.forEach((key, index) => {
      orderMap[key] = index;
    });

    // Only display components specified in the array
    const filteredComponentList = componentList.filter((componentItem) =>
      sectionsOrder.includes(componentItem.key),
    );

    // Sort components based on their order in the array
    const orderedComponentList = filteredComponentList.sort(
      (componentItemA, componentItemB) => {
        const orderA = orderMap[componentItemA.key];
        const orderB = orderMap[componentItemB.key];
        return orderA - orderB;
      },
    );

    if (returnObjectList) {
      return orderedComponentList as T extends true ? ComponentItem[] : never;
    }

    return orderedComponentList.map((orderedComponentItem) => {
      return orderedComponentItem.renderer();
    }) as T extends true ? never : (JSX.Element | null)[];
  }

  // Handle original object format ({ [key: string]: number })
  else {
    // 0 means component should be hidden
    const filteredComponentList = componentList.filter(
      (componentItem) => sectionsOrder[componentItem.key] !== 0,
    );

    const orderedComponentList = filteredComponentList.sort(
      (componentItemA, componentItemB) => {
        const orderA = sectionsOrder[componentItemA.key];
        const orderB = sectionsOrder[componentItemB.key];
        // If a.order is undefined, it should come after b
        if (orderA === undefined) return 1;
        // If b.order is undefined, it should come after a
        if (orderB === undefined) return -1;
        // If both have defined order values, sort normally
        return orderA - orderB;
      },
    );

    if (returnObjectList) {
      return orderedComponentList as T extends true ? ComponentItem[] : never;
    }

    return orderedComponentList.map((orderedComponentItem) => {
      return orderedComponentItem.renderer();
    }) as T extends true ? never : (JSX.Element | null)[];
  }
}

export default getSortedComponentsBySectionOrder;
